import { repositoryFactory } from '@data';

/**
 * Adaptive Session State Manager Service
 * Manages adaptive learning session state including:
 * - Linear progression tracking
 * - Remediation cooldowns and triggers
 * - Recent answer history for performance calculation
 * - Dynamic question group sizing
 */
export class AdaptiveSessionStateManager {
  constructor() {
    this.sessionRepository = repositoryFactory.getSessionRepository();
    this.progressRepository = repositoryFactory.getProgressRepository();
  }

  /**
   * Initialize or get session state
   * Creates new session state if doesn't exist
   */
  async getOrCreateSessionState(userId, courseId, sectionId) {
    try {
      let session = await this.sessionRepository.getActiveSession(userId, courseId);
      
      if (!session) {
        session = await this.sessionRepository.createSession(userId, courseId, sectionId);
        session.linearProgressionPoint = sectionId;
        session.injectedSectionIds = [];
        session.nextQuestionGroupSize = 5; // Default starting size
        session.remediationCooldowns = {};
        session.consecutiveMissesByTag = {};
        session.recentAnswerHistory = [];
        await this.sessionRepository.updateSession(session);
      }
      
      return session;
    } catch (err) {
      console.error('[AdaptiveSessionStateManager] Error getting session:', err);
      throw err;
    }
  }

  /**
   * Record recent answer for performance window calculation
   * Maintains sliding window of last 10-15 answers
   */
  async recordRecentAnswer(sessionId, questionId, tagIds, isCorrect, timestamp = new Date()) {
    try {
      const session = await this.sessionRepository.getSessionById(sessionId);
      if (!session) throw new Error(`Session not found: ${sessionId}`);

      // Parse recent answer history
      let recentAnswers = [];
      try {
        recentAnswers = typeof session.recentAnswerHistory === 'string' 
          ? JSON.parse(session.recentAnswerHistory) 
          : session.recentAnswerHistory;
      } catch (e) {
        recentAnswers = [];
      }

      // Add new answer
      recentAnswers.push({
        questionId,
        tagIds: Array.isArray(tagIds) ? tagIds : [tagIds],
        isCorrect,
        timestamp: timestamp.toISOString(),
      });

      // Keep only last 15 answers
      if (recentAnswers.length > 15) {
        recentAnswers = recentAnswers.slice(-15);
      }

      session.recentAnswerHistory = JSON.stringify(recentAnswers);
      await this.sessionRepository.updateSession(session);

      return recentAnswers;
    } catch (err) {
      console.error('[AdaptiveSessionStateManager] Error recording answer:', err);
      throw err;
    }
  }

  /**
   * Calculate recent performance (last 10-15 answers)
   * Used to determine dynamic question group sizing
   * Returns: { correctCount, totalCount, correctRate, recommendation }
   */
  async calculateRecentPerformance(sessionId) {
    try {
      const session = await this.sessionRepository.getSessionById(sessionId);
      if (!session) throw new Error(`Session not found: ${sessionId}`);

      let recentAnswers = [];
      try {
        recentAnswers = typeof session.recentAnswerHistory === 'string' 
          ? JSON.parse(session.recentAnswerHistory) 
          : session.recentAnswerHistory;
      } catch (e) {
        recentAnswers = [];
      }

      if (recentAnswers.length === 0) {
        return {
          correctCount: 0,
          totalCount: 0,
          correctRate: 0.5, // Default 50% for new sessions
          recommendation: 'increase', // Start normal
          sampleSize: 0,
        };
      }

      const correctCount = recentAnswers.filter(a => a.isCorrect).length;
      const totalCount = recentAnswers.length;
      const correctRate = correctCount / totalCount;

      // Recommendation based on performance
      let recommendation = 'maintain';
      if (correctRate >= 0.8) {
        recommendation = 'increase'; // High performer: increase group size
      } else if (correctRate < 0.6) {
        recommendation = 'decrease'; // Struggling: decrease group size
      }

      return {
        correctCount,
        totalCount,
        correctRate,
        recommendation,
        sampleSize: totalCount,
      };
    } catch (err) {
      console.error('[AdaptiveSessionStateManager] Error calculating performance:', err);
      return {
        correctCount: 0,
        totalCount: 0,
        correctRate: 0.5,
        recommendation: 'maintain',
        sampleSize: 0,
      };
    }
  }

  /**
   * Update dynamic question group size based on performance
   * Base: 5 questions per group
   * Range: 3-20 questions
   * - If correctRate >= 0.8: increase to 7-20
   * - If correctRate 0.6-0.8: maintain 5-7
   * - If correctRate < 0.6: decrease to 3-5
   */
  async updateQuestionGroupSize(sessionId) {
    try {
      const performance = await this.calculateRecentPerformance(sessionId);
      const session = await this.sessionRepository.getSessionById(sessionId);
      if (!session) throw new Error(`Session not found: ${sessionId}`);

      let newGroupSize = 5; // Default

      if (performance.recommendation === 'increase') {
        // High performer: gradually increase from 5 to 20
        newGroupSize = Math.min(20, Math.floor(5 + (performance.correctRate - 0.8) * 75)); // 5 to 20
      } else if (performance.recommendation === 'decrease') {
        // Struggling: gradually decrease from 5 to 3
        newGroupSize = Math.max(3, Math.floor(5 - (0.6 - performance.correctRate) * 13.33)); // 5 to 3
      }

      session.nextQuestionGroupSize = newGroupSize;
      await this.sessionRepository.updateSession(session);

      console.log('[AdaptiveSessionStateManager] Group size updated:', {
        correctRate: performance.correctRate,
        recommendation: performance.recommendation,
        newGroupSize,
      });

      return newGroupSize;
    } catch (err) {
      console.error('[AdaptiveSessionStateManager] Error updating group size:', err);
      return 5; // Default on error
    }
  }

  /**
   * Track consecutive misses by tag
   * Used to trigger remediation on 3-5 consecutive incorrect answers
   */
  async updateConsecutiveMisses(sessionId, tagId, isCorrect) {
    try {
      const session = await this.sessionRepository.getSessionById(sessionId);
      if (!session) throw new Error(`Session not found: ${sessionId}`);

      let missesByTag = {};
      try {
        missesByTag = typeof session.consecutiveMissesByTag === 'string' 
          ? JSON.parse(session.consecutiveMissesByTag) 
          : session.consecutiveMissesByTag;
      } catch (e) {
        missesByTag = {};
      }

      if (isCorrect) {
        // Reset on correct answer
        missesByTag[tagId] = 0;
      } else {
        // Increment on incorrect
        missesByTag[tagId] = (missesByTag[tagId] || 0) + 1;
      }

      session.consecutiveMissesByTag = JSON.stringify(missesByTag);
      await this.sessionRepository.updateSession(session);

      return {
        tagId,
        consecutiveIncorrect: missesByTag[tagId],
        shouldTriggerRemediation: missesByTag[tagId] >= 3,
      };
    } catch (err) {
      console.error('[AdaptiveSessionStateManager] Error updating consecutive misses:', err);
      throw err;
    }
  }

  /**
   * Check if remediation is in cooldown for a tag
   * Prevents over-triggering same remediation
   * Cooldown: 30 minutes (1800000 ms) after last trigger
   */
  isRemediationOnCooldown(session, tagId, cooldownMs = 30 * 60 * 1000) {
    try {
      let cooldowns = {};
      try {
        cooldowns = typeof session.remediationCooldowns === 'string' 
          ? JSON.parse(session.remediationCooldowns) 
          : session.remediationCooldowns;
      } catch (e) {
        cooldowns = {};
      }

      if (!cooldowns[tagId]) {
        return false;
      }

      const lastTrigger = new Date(cooldowns[tagId]);
      const timeSinceTrigger = Date.now() - lastTrigger.getTime();
      
      return timeSinceTrigger < cooldownMs;
    } catch (err) {
      console.error('[AdaptiveSessionStateManager] Error checking cooldown:', err);
      return false;
    }
  }

  /**
   * Set remediation cooldown for a tag
   */
  async setRemediationCooldown(sessionId, tagId, timestamp = new Date()) {
    try {
      const session = await this.sessionRepository.getSessionById(sessionId);
      if (!session) throw new Error(`Session not found: ${sessionId}`);

      let cooldowns = {};
      try {
        cooldowns = typeof session.remediationCooldowns === 'string' 
          ? JSON.parse(session.remediationCooldowns) 
          : session.remediationCooldowns;
      } catch (e) {
        cooldowns = {};
      }

      cooldowns[tagId] = timestamp.toISOString();
      session.remediationCooldowns = JSON.stringify(cooldowns);
      await this.sessionRepository.updateSession(session);

      console.log('[AdaptiveSessionStateManager] Remediation cooldown set for tag:', { tagId });
      return cooldowns;
    } catch (err) {
      console.error('[AdaptiveSessionStateManager] Error setting cooldown:', err);
      throw err;
    }
  }

  /**
   * Inject a section out-of-order for remediation/review
   * Preserves linear progression point so we can resume after
   */
  async injectSection(sessionId, sectionIdToInject, currentLinearSection) {
    try {
      const session = await this.sessionRepository.getSessionById(sessionId);
      if (!session) throw new Error(`Session not found: ${sessionId}`);

      // Record linear progression if not already set
      if (!session.linearProgressionPoint) {
        session.linearProgressionPoint = currentLinearSection;
      }

      // Add to injected sections
      let injected = Array.isArray(session.injectedSectionIds) 
        ? session.injectedSectionIds 
        : [];
      
      if (!injected.includes(sectionIdToInject)) {
        injected.push(sectionIdToInject);
      }

      session.injectedSectionIds = injected;
      await this.sessionRepository.updateSession(session);

      console.log('[AdaptiveSessionStateManager] Section injected:', {
        injected: sectionIdToInject,
        linearProgressionPoint: session.linearProgressionPoint,
      });

      return {
        injectedSectionIds: injected,
        linearProgressionPoint: session.linearProgressionPoint,
      };
    } catch (err) {
      console.error('[AdaptiveSessionStateManager] Error injecting section:', err);
      throw err;
    }
  }

  /**
   * Resume linear progression after injection
   * Returns to linearProgressionPoint
   */
  async resumeLinearProgression(sessionId) {
    try {
      const session = await this.sessionRepository.getSessionById(sessionId);
      if (!session) throw new Error(`Session not found: ${sessionId}`);

      const resumePoint = session.linearProgressionPoint;
      
      // Clear injected sections after resuming
      session.injectedSectionIds = [];
      await this.sessionRepository.updateSession(session);

      console.log('[AdaptiveSessionStateManager] Resumed linear progression:', {
        resumePoint,
      });

      return resumePoint;
    } catch (err) {
      console.error('[AdaptiveSessionStateManager] Error resuming progression:', err);
      throw err;
    }
  }

  /**
   * Update section-level remediation tracking
   * Called when remediation is triggered for a section
   */
  async recordSectionRemediation(userId, courseId, sectionId) {
    try {
      const progress = await this.progressRepository.getUserProgress(userId, courseId);
      if (!progress) throw new Error('Progress not found');

      const sectionProgress = progress.getSectionProgress(sectionId);
      if (!sectionProgress) throw new Error(`Section progress not found: ${sectionId}`);

      sectionProgress.consecutiveIncorrect = 0; // Reset on remediation
      sectionProgress.lastRemediationTriggered = new Date();
      sectionProgress.remediationCount = (sectionProgress.remediationCount || 0) + 1;

      await this.progressRepository.updateProgress(progress);

      return sectionProgress;
    } catch (err) {
      console.error('[AdaptiveSessionStateManager] Error recording section remediation:', err);
      throw err;
    }
  }
}

// Singleton instance
export const adaptiveSessionStateManager = new AdaptiveSessionStateManager();
